const DEFAULT_HEADING = "Nu har du kommit till gränsen för vårt exempel.";

const HEADINGS = {
  HANDLE_PROPERTIES: "Hantera dina egna fastigheter",
  SEE_IN_YOUR_FOREST: "Se detta i din egen skog",
  GET_ADVICE: "Få rådgivning för din egen skog",
  SELL_TIMBER: "Sälj virke från din egen skog",
  SELL_BIOFUEL: "Sälj biobränsle från din egen skog",
  ORDER_DRONE: "Beställ drönarflygning för din skog",
  CARBON_CREDITS: "Se din kolkreditpotential",
  READ_MARKET_INFO: "Läs marknadsinformation",
  FOREST_ADVICE: "Skoglig expertis till stöd i ditt skogsägande",
  PRICE_CHECK: "Vi analyserar ditt avverkningsförslag och hjälper dig få rätt betalt",
  CHANGE_VOLUME: "Ändra volym i din egen fastighet",
  CHANGE_GROWTH: "Ändra bonitet i din egen fastighet",
  UPDATE_FOREST_PLAN: "Uppdatera din skogsbruksplan",
} as const;

export type DemoPrompt = {
  heading: string;
  message: string;
};

export const DEMO_PROMPTS = {
  // Account & profile
  MANAGE_ACCOUNT: {
    heading: HEADINGS.HANDLE_PROPERTIES,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att ändra och spara dina profilinställningar på ditt konto.",
  },
  MANAGE_PROFILE: {
    heading: HEADINGS.HANDLE_PROPERTIES,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att ändra och spara dina profilinställningar på ditt konto.",
  },

  // Property selector
  ADD_PROPERTIES: {
    heading: HEADINGS.HANDLE_PROPERTIES,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att lägga till dina egna fastigheter.",
  },

  // Navigation / redirects
  BOOK_MEETING: {
    heading: HEADINGS.GET_ADVICE,
    message:
      "Detta är ett exempel. Logga in för att boka rådgivning gällande till din skog och ditt skogsbruk. Detta kräver att du är Pluskund.",
  },
  VIRKESFORSALJNING: {
    heading: HEADINGS.SELL_TIMBER,
    message:
      "På exempelfastigheten kan du inte sälja virke. Logga in för att konkurrensutsätta ditt virke, nå alla potentiella köpare och få rättvist betalt för ditt virke i din skog.",
  },
  BIOBRANSLE: {
    heading: HEADINGS.SELL_BIOFUEL,
    message:
      "På exempelfastigheten kan du inte sälja virke. Logga in för att konkurrensutsätta ditt biobränsle, nå alla potentiella köpare och få rättvist betalt för ditt virke i din skog.",
  },
  DRONE_ORDER: {
    heading: HEADINGS.ORDER_DRONE,
    message:
      "Du tittar just nu på en exempelfastighet. Logga in för att beställa en drönarflyging och analys för din skog. Detta kräver att du är Pluskund.",
  },
  KOLKREDITER: {
    heading: HEADINGS.CARBON_CREDITS,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att läsa mer och se om du har potential för kolkrediter i din skog samt vilka intäkter som är möjliga. Detta kräver att du är Pluskund.",
  },
  FOREST_ADVICE: {
    heading: HEADINGS.FOREST_ADVICE,
    message:
      "Vi besitter djup kunskap om skog, virkespriser m.m. Vi hjälper dig gärna i ditt skogsägande men tjänsten kräver att du är Pluskund",
  },
  VIRKESPRISKOLLEN: {
    heading: HEADINGS.PRICE_CHECK,
    message:
      "Med vår Virkespriskoll får du hjälp att säkerställa pris och villkor för din virkesaffär.",
  },

  // Articles / kunskapscentrum
  ARTICLES: {
    heading: HEADINGS.READ_MARKET_INFO,
    message:
      "Du tittar just nu på en exempelfastighet. Här visas endast en begränsad version. Logga in för att se alla händelser, läsa marknadsinformation och ta del av hela artiklar med full marknadsinsikt.",
  },
  READ_ARTICLE: {
    heading: HEADINGS.READ_MARKET_INFO,
    message:
      "Du tittar just nu på en exempelfastighet. Detta är ett utdrag från demon. Logga in för att läsa hela artikeln eller blogginlägget och få full marknadsinsikt.",
  },

  // Map actions
  UPDATE_MARKER: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att uppdatera en markör i din skog. Detta kräver att du är Pluskund.",
  },
  CREATE_MARKER: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att placera en markör i din skog. Detta kräver att du är Pluskund.",
  },
  CREATE_SECTION: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att skapa en ny avdelning i din skog. Detta kräver att du är Pluskund.",
  },

  // Generic save / actions
  SAVE_CHANGES: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att spara ändringarna på avdelningen i din skog. Detta kräver att du är Pluskund.",
  },
  CREATE_AD: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. För att sälja virke behöver du logga in för att påbörja din virkesannons.",
  },
  RUN_TOOL: {
    heading: HEADINGS.SEE_IN_YOUR_FOREST,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att använda något av verktygen i lista i din skog. Detta kräver att du är Pluskund.",
  },
  UPDATE_FOREST_PLAN: {
    heading: HEADINGS.CHANGE_VOLUME,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att ändra volymen i din skog. Detta kräver att du är Pluskund.",
  },
  UPDATE_AVG_GROWTH: {
    heading: HEADINGS.CHANGE_GROWTH,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att ändra bonitet i din skog. Detta kräver att du är Pluskund.",
  },
  UPDATE_VOLUME_AND_GROWTH: {
    heading: HEADINGS.UPDATE_FOREST_PLAN,
    message:
      "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att uppdatera volym och bonitet i din skog. Detta kräver att du är Pluskund.",
  },
} as const;

export type DemoPromptKey = keyof typeof DEMO_PROMPTS;

export const getDemoPromptMessage = (prompt: DemoPrompt | string): string => {
  if (typeof prompt === "string") return prompt;
  return prompt.message;
};

export const getDemoPromptHeading = (prompt: DemoPrompt | string): string => {
  if (typeof prompt === "string") return DEFAULT_HEADING;
  return prompt.heading;
};
